import React, { useMemo } from 'react';
import { Form, Select, Alert, Tag, Tooltip } from 'antd';
import { useLanguage } from '../contexts/LanguageContext';

const TARGET_FIELDS = {
  customers: [
    { key: 'customerNo', required: true },
    { key: 'branchNo' },
    { key: 'name1' },
    { key: 'name2' },
    { key: 'name3' },
    { key: 'nameLocaleLine1ZhTc' },
    { key: 'nameLocaleLine2ZhTc' },
    { key: 'nameLocaleLine3ZhTc' },
    { key: 'nameLocaleLine1ZhSc' },
    { key: 'nameLocaleLine2ZhSc' },
    { key: 'nameLocaleLine3ZhSc' },
    { key: 'contactName' },
    { key: 'tel' },
    { key: 'email' },
    { key: 'whatsappNo' },
    { key: 'salesmanName' },
    { key: 'salesmanCode' },
    { key: 'salesmanWhatsapp' },
    { key: 'creditLimit' },
    { key: 'priceType' },
    { key: 'billingAddressEn1' },
    { key: 'billingAddressEn2' },
    { key: 'billingAddressEn3' },
    { key: 'billingAddressCn1' },
    { key: 'billingAddressCn2' },
    { key: 'billingAddressCn3' },
    { key: 'deliveryAddressEn1' },
    { key: 'deliveryAddressEn2' },
    { key: 'deliveryAddressEn3' },
    { key: 'deliveryAddressCn1' },
    { key: 'deliveryAddressCn2' },
    { key: 'deliveryAddressCn3' },
    { key: 'deliveryDayMon' },
    { key: 'deliveryDayTue' },
    { key: 'deliveryDayWed' },
    { key: 'deliveryDayThu' },
    { key: 'deliveryDayFri' },
    { key: 'deliveryDaySat' },
    { key: 'deliveryDaySun' },
    { key: 'deliveryDayHoliday' },
    { key: 'status' },
  ],
  priceCards: [
    { key: 'customerNo', required: true },
    { key: 'branchNo' },
    { key: 'itemNo', required: true },
    { key: 'unitPrice', required: true },
    { key: 'showPrice' },
    { key: 'validFrom' },
    { key: 'validTo' },
    { key: 'priceType' },
    { key: 'source' },
  ],
  products: [
    { key: 'name', required: true },
    { key: 'nameLocaleEn' },
    { key: 'nameLocaleZhTc' },
    { key: 'nameLocaleZhSc' },
    { key: 'productRetailerId', required: true },
    { key: 'description' },
    { key: 'linkUrl' },
    { key: 'imageUrl' },
    { key: 'brand' },
    { key: 'price' },
    { key: 'price1' },
    { key: 'validFrom1' },
    { key: 'validTo1' },
    { key: 'price2' },
    { key: 'validFrom2' },
    { key: 'validTo2' },
    { key: 'currency' },
    { key: 'status' },
    { key: 'availability' },
    { key: 'unitOfMeasureLocaleEn' },
    { key: 'unitOfMeasureLocaleZhTc' },
    { key: 'unitOfMeasureLocaleZhSc' },
    { key: 'unitOfBrokenPackLocaleEn' },
    { key: 'unitOfBrokenPackLocaleZhTc' },
    { key: 'unitOfBrokenPackLocaleZhSc' },
    { key: 'qtyPerUnit' },
    { key: 'minOrderQuantity' },
    { key: 'oqm' },
    { key: 'condition' },
    { key: 'color' },
    { key: 'size' },
    { key: 'gender' },
    { key: 'material' },
    { key: 'pattern' },
    { key: 'customLabel0' },
    { key: 'productionCategory' },
    { key: 'metaCatalogId' },
  ],
  collections: [
    { key: 'name', required: true },
    { key: 'nameLocaleEn' },
    { key: 'nameLocaleZhTc' },
    { key: 'nameLocaleZhSc' },
    { key: 'externalCollectionKey' },
    { key: 'description' },
    { key: 'metaCatalogId' },
  ],
  collectionLinks: [
    { key: 'collectionKey', required: true },
    { key: 'productRetailerId', required: true },
  ],
  wpaGroupMembers: [
    { key: 'groupName', required: true },
    { key: 'loginId', required: true },
  ],
  wpaGroupCustomers: [
    { key: 'groupName', required: true },
    { key: 'customerNo', required: true },
    { key: 'branchNo' },
  ],
  wpaContactOverrides: [
    { key: 'loginId', required: true },
    { key: 'roleKey', required: true },
    { key: 'defaultCustomerNo' },
    { key: 'defaultBranchNo' },
    { key: 'customerNos' },
    { key: 'status' },
  ],
  wpaIdentities: [
    { key: 'loginId', required: true },
    { key: 'email' },
    { key: 'phoneNo' },
    { key: 'displayName' },
    { key: 'password' },
    { key: 'status' },
  ],
};

const FIELD_I18N_MAP = {
  customerNo: 'fieldCustomerNo',
  branchNo: 'fieldBranchNo',
  name1: 'fieldCustomerName1',
  name2: 'fieldCustomerName2',
  name3: 'fieldCustomerName3',
  nameLocaleLine1ZhTc: 'fieldCustomerNameLine1ZhTc',
  nameLocaleLine1ZhSc: 'fieldCustomerNameLine1ZhSc',
  nameLocaleLine2ZhTc: 'fieldCustomerNameLine2ZhTc',
  nameLocaleLine2ZhSc: 'fieldCustomerNameLine2ZhSc',
  nameLocaleLine3ZhTc: 'fieldCustomerNameLine3ZhTc',
  nameLocaleLine3ZhSc: 'fieldCustomerNameLine3ZhSc',
  contactName: 'fieldContactName',
  tel: 'fieldTel',
  email: 'fieldEmail',
  whatsappNo: 'fieldWhatsappNo',
  salesmanName: 'fieldSalesmanName',
  salesmanCode: 'fieldSalesmanCode',
  salesmanWhatsapp: 'fieldSalesmanWhatsapp',
  creditLimit: 'fieldCreditLimit',
  priceType: 'fieldPriceType',
  billingAddressEn1: 'fieldBillingAddressEn1',
  billingAddressEn2: 'fieldBillingAddressEn2',
  billingAddressEn3: 'fieldBillingAddressEn3',
  billingAddressCn1: 'fieldBillingAddressCn1',
  billingAddressCn2: 'fieldBillingAddressCn2',
  billingAddressCn3: 'fieldBillingAddressCn3',
  deliveryAddressEn1: 'fieldDeliveryAddressEn1',
  deliveryAddressEn2: 'fieldDeliveryAddressEn2',
  deliveryAddressEn3: 'fieldDeliveryAddressEn3',
  deliveryAddressCn1: 'fieldDeliveryAddressCn1',
  deliveryAddressCn2: 'fieldDeliveryAddressCn2',
  deliveryAddressCn3: 'fieldDeliveryAddressCn3',
  deliveryDayMon: 'fieldDeliveryDayMon',
  deliveryDayTue: 'fieldDeliveryDayTue',
  deliveryDayWed: 'fieldDeliveryDayWed',
  deliveryDayThu: 'fieldDeliveryDayThu',
  deliveryDayFri: 'fieldDeliveryDayFri',
  deliveryDaySat: 'fieldDeliveryDaySat',
  deliveryDaySun: 'fieldDeliveryDaySun',
  deliveryDayHoliday: 'fieldDeliveryDayHoliday',
  itemNo: 'fieldItemNo',
  unitPrice: 'fieldUnitPrice',
  showPrice: 'fieldShowPrice',
  validFrom: 'fieldValidFrom',
  validTo: 'fieldValidTo',
  source: 'fieldSource',
  name: 'fieldName',
  nameLocaleEn: 'fieldNameLocaleEn',
  nameLocaleZhTc: 'fieldNameLocaleZhTc',
  nameLocaleZhSc: 'fieldNameLocaleZhSc',
  productRetailerId: 'fieldRetailerId',
  description: 'fieldDescription',
  linkUrl: 'fieldLinkUrl',
  imageUrl: 'fieldImageUrl',
  brand: 'fieldBrand',
  price: 'fieldPrice',
  price1: 'fieldProductPrice1',
  validFrom1: 'fieldProductValidFrom1',
  validTo1: 'fieldProductValidTo1',
  price2: 'fieldProductPrice2',
  validFrom2: 'fieldProductValidFrom2',
  validTo2: 'fieldProductValidTo2',
  currency: 'fieldCurrency',
  status: 'fieldStatus',
  availability: 'fieldAvailability',
  unitOfMeasure: 'fieldUnitOfMeasure',
  unitOfMeasureLocaleEn: 'fieldUnitOfMeasureLocaleEn',
  unitOfMeasureLocaleZhTc: 'fieldUnitOfMeasureLocaleZhTc',
  unitOfMeasureLocaleZhSc: 'fieldUnitOfMeasureLocaleZhSc',
  unitOfBrokenPackLocaleEn: 'fieldUnitOfBrokenPackLocaleEn',
  unitOfBrokenPackLocaleZhTc: 'fieldUnitOfBrokenPackLocaleZhTc',
  unitOfBrokenPackLocaleZhSc: 'fieldUnitOfBrokenPackLocaleZhSc',
  qtyPerUnit: 'fieldQtyPerUnit',
  minOrderQuantity: 'fieldMinOrderQuantity',
  oqm: 'fieldOqm',
  condition: 'fieldCondition',
  color: 'fieldColor',
  size: 'fieldSize',
  gender: 'fieldGender',
  material: 'fieldMaterial',
  pattern: 'fieldPattern',
  customLabel0: 'fieldCustomLabel0',
  productionCategory: 'fieldProductionCategory',
  metaCatalogId: 'fieldMetaCatalogId',
  externalCollectionKey: 'fieldExternalKey',
  collectionKey: 'fieldCollectionKey',
  groupName: 'fieldGroupName',
  whatsappNumber: 'fieldWhatsappNumber',
  roleKey: 'fieldRoleKey',
  defaultCustomerNo: 'fieldDefaultCustomerNo',
  defaultBranchNo: 'fieldDefaultBranchNo',
  customerNos: 'fieldCustomerNos',
  phoneNo: 'fieldPhoneNo',
  displayName: 'fieldDisplayName',
  password: 'fieldPassword',
  loginId: 'fieldLoginId',
};

const HINT_I18N_MAP = {
  customers: 'hintCustomers',
  priceCards: 'hintPriceCards',
  products: 'hintProducts',
  collections: 'hintCollections',
  collectionLinks: 'hintCollectionLinks',
  wpaGroupMembers: 'hintWpaGroupMembers',
  wpaGroupCustomers: 'hintWpaGroupCustomers',
  wpaContactOverrides: 'hintWpaContactOverrides',
  wpaIdentities: 'hintWpaIdentities',
};

const CommerceImportFieldMapping = ({ form, columns = [], targetEntity }) => {
  const { t } = useLanguage();

  const fields = useMemo(() => TARGET_FIELDS[targetEntity] || [], [targetEntity]);
  const hintKey = HINT_I18N_MAP[targetEntity];
  const hint = hintKey ? t(`commerceImport.${hintKey}`) : null;

  const columnOptions = useMemo(
    () => [
      { label: t('commerceImport.noColumnMapping'), value: '' },
      ...columns.map((col) => ({
        label: col.title || col.dataIndex,
        value: col.dataIndex,
      })),
    ],
    [columns, t]
  );

  const localeTag = (key) => {
    if (key === 'nameLocaleEn') return <Tag style={{ marginRight: 6 }}>EN</Tag>;
    if (key === 'nameLocaleZhTc') return <Tooltip title="zh-TC"><Tag style={{ marginRight: 6 }}>繁</Tag></Tooltip>;
    if (key === 'nameLocaleZhSc') return <Tooltip title="zh-SC"><Tag style={{ marginRight: 6 }}>简</Tag></Tooltip>;
    if (key === 'unitOfMeasureLocaleEn') return <Tag style={{ marginRight: 6 }}>EN</Tag>;
    if (key === 'unitOfMeasureLocaleZhTc') return <Tooltip title="zh-TC"><Tag style={{ marginRight: 6 }}>繁</Tag></Tooltip>;
    if (key === 'unitOfMeasureLocaleZhSc') return <Tooltip title="zh-SC"><Tag style={{ marginRight: 6 }}>简</Tag></Tooltip>;
    if (key === 'unitOfBrokenPackLocaleEn') return <Tag style={{ marginRight: 6 }}>EN</Tag>;
    if (key === 'unitOfBrokenPackLocaleZhTc') return <Tooltip title="zh-TC"><Tag style={{ marginRight: 6 }}>繁</Tag></Tooltip>;
    if (key === 'unitOfBrokenPackLocaleZhSc') return <Tooltip title="zh-SC"><Tag style={{ marginRight: 6 }}>简</Tag></Tooltip>;
    if (key === 'name1' || key === 'name2' || key === 'name3') return <Tag style={{ marginRight: 6 }}>EN</Tag>;
    if (
      key === 'nameLocaleLine1ZhTc' ||
      key === 'nameLocaleLine2ZhTc' ||
      key === 'nameLocaleLine3ZhTc'
    ) {
      return (
        <Tooltip title="zh-TC">
          <Tag style={{ marginRight: 6 }}>繁</Tag>
        </Tooltip>
      );
    }
    if (
      key === 'nameLocaleLine1ZhSc' ||
      key === 'nameLocaleLine2ZhSc' ||
      key === 'nameLocaleLine3ZhSc'
    ) {
      return (
        <Tooltip title="zh-SC">
          <Tag style={{ marginRight: 6 }}>简</Tag>
        </Tooltip>
      );
    }
    const dayTags = {
      deliveryDayMon: '一',
      deliveryDayTue: '二',
      deliveryDayWed: '三',
      deliveryDayThu: '四',
      deliveryDayFri: '五',
      deliveryDaySat: '六',
      deliveryDaySun: '日',
      deliveryDayHoliday: '假',
    };
    if (dayTags[key]) return <Tag style={{ marginRight: 6 }}>{dayTags[key]}</Tag>;
    return null;
  };

  const fieldLabel = (key) => {
    if (key === 'productRetailerId') return t('commerceProducts.contentId');
    const i18nKey = FIELD_I18N_MAP[key];
    return i18nKey ? t(`commerceImport.${i18nKey}`) : key;
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', paddingRight: 8 }}>
      {hint && <Alert message={hint} type="info" showIcon style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical">
        {fields.map(f => (
          <Form.Item
            key={f.key}
            label={
              <span style={f.required ? { color: '#ff4d4f', fontWeight: 600 } : undefined}>
                {localeTag(f.key)}
                {fieldLabel(f.key)}
              </span>
            }
            name={f.key}
            required={!!f.required}
            rules={
              f.required
                ? [
                    {
                      validator: (_, value) =>
                        value !== undefined && value !== null && value !== ''
                          ? Promise.resolve()
                          : Promise.reject(new Error(t('commerceImport.fieldMappingColumnRequired'))),
                    },
                  ]
                : []
            }
            validateTrigger={['onChange', 'onBlur']}
          >
            <Select
              placeholder={t('contactImport.selectColumn')}
              allowClear
              showSearch
              options={columnOptions}
              optionFilterProp="label"
            />
          </Form.Item>
        ))}
      </Form>
    </div>
  );
};

export { TARGET_FIELDS, FIELD_I18N_MAP };
export default CommerceImportFieldMapping;
